import React from 'react';
import { useI18n } from '@/contexts/I18nContext';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronDown } from 'lucide-react';
import { RatingSelector } from '@/components/ui/RatingSelector';

export interface ITrainingNotesFormProps {
  fullNotes: string;
  setFullNotes: (notes: string) => void;
  shareWithCommunity: boolean;
  setShareWithCommunity: (share: boolean) => void;
  privacyLevel: 'public' | 'connections' | 'gym';
  setPrivacyLevel: (level: 'public' | 'connections' | 'gym') => void;
  overallRating: number | null;
  setOverallRating: (rating: number | null) => void;
  saving: boolean;
}

export const TrainingNotesForm: React.FC<ITrainingNotesFormProps> = ({
  fullNotes,
  setFullNotes,
  shareWithCommunity,
  setShareWithCommunity,
  privacyLevel,
  setPrivacyLevel,
  overallRating,
  setOverallRating,
  saving,
}) => {
  const { t } = useI18n();

  return (
    <>
      {/* Overall Rating - Hidden but logic preserved */}
      {false && (
        <div className="space-y-3 !mt-8">
          <label className="block text-sm font-medium text-black">
            {t('calendar.overall_rating')}
          </label>
          <RatingSelector
            value={overallRating}
            onChange={(rating) => {
              setOverallRating(rating);
            }}
            disabled={saving}
          />
        </div>
      )}

      {/* Full Training Notes with Share with Community */}
      <div>
        <label className="block text-sm font-medium mb-2 mt-8 text-black">
          {t('calendar.full_training_notes')}
          <span className="text-gray-500 text-xs ml-1">
            ({fullNotes.length}/2000)
          </span>
        </label>
        
        <Textarea
          value={fullNotes}
          onChange={(e) => setFullNotes(e.target.value.slice(0, 2000))}
          placeholder={t('calendar.detailed_reflections')}
          className="text-black border-gray-300 bg-white resize-none text-sm"
          rows={8}
          maxLength={2000}
        />
        
        {/* Share with Community */}
        <div className="space-y-2 mt-3">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="share-community"
              checked={shareWithCommunity}
              onCheckedChange={(checked) => setShareWithCommunity(checked as boolean)}
              disabled={saving || !fullNotes.trim()}
            />
            <label htmlFor="share-community" className={`text-sm font-medium ${!fullNotes.trim() ? 'text-gray-400' : 'text-black'}`}>
              {t('calendar.share_with_community')}
            </label>
          </div>

          {shareWithCommunity && (
            <div className="!mt-5">
              <label className="block text-xs text-black">{t('calendar.who_can_see_post')}</label>
              <div className="w-full">
                <Select value={privacyLevel} onValueChange={(value: 'public' | 'connections' | 'gym') => setPrivacyLevel(value)}>
                  <SelectTrigger className="w-full h-8 text-xs text-black relative [&_[data-radix-select-icon]]:hidden">
                    <SelectValue />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                      <ChevronDown className="w-4 h-4 text-black" />
                    </span>
                  </SelectTrigger>
                  <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)] bg-black border-0 py-2 space-y-1">
                    <SelectItem value="public" className="text-white truncate">{t('social.whole_community')}</SelectItem>
                    <SelectItem value="connections" className="text-white truncate">{t('calendar.only_my_connections')}</SelectItem>
                    <SelectItem value="gym" className="text-white truncate">{t('calendar.only_my_gym')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};


